<template>
  <div class="relative flex items-center p-5 m-1 bg-gray-100 rounded-md hover:bg-gray-200 group">
    <div class="flex-1 block p-1 -m-1">
      <div class="absolute inset-0 group-hover:bg-gray-100 dark:group-hover:bg-dark-divider" aria-hidden="true" />
      <div class="relative flex items-center flex-1 min-w-0">
        <span class="relative flex-shrink-0 inline-block">
          <Avatar :first-name="group.name" :is-company="true" />
        </span>
        <div class="ml-4 truncate">
          <p class="text-sm font-medium text-gray-900 truncate dark:text-white">
            {{ group.name }} ({{ $t("Group") }})
          </p>
          <p v-if="!group.unselected_contacts.length" class="text-sm text-gray-500 truncate">
            {{ group.contacts_count }} {{ $t("contacts") }}
          </p>
          <p v-else class="text-sm text-gray-500 truncate">
            {{ group.contacts_count - group.unselected_contacts.length }}/{{ group.contacts_count }} {{ $t("contacts selected") }}
          </p>
        </div>
      </div>
    </div>
    <div as="div" class="relative flex-shrink-0 inline-block ml-5 text-left">
      <slot name="actions">
        <SimpleActionDropDown
          v-if="canShowDropdown"
          classes="relative flex-shrink-0 top-1"
          dropdown-class="absolute z-50 w-48 mx-3 mt-1 !w-32 !-right-6 origin-bottom-right bg-white rounded-md shadow-lg dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 focus:outline-none"
          item-class="text-gray-700 dark:text-dark-body-text"
          item-active-class="text-gray-400 bg-gray-100 dark:bg-dark-screen-background"
          :icon="ellipsisVerticalIcon"
          :icon-button="true"
          :items="dropdownItems" />
      </slot>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from "vue"
import SimpleActionDropDown from "@/components/shared/dropdowns/SimpleActionDropDown.vue"
import Avatar from "@/components/shared/Avatar.vue"
import { useI18n } from "vue-i18n"

export default defineComponent({
  name: "GroupCard",
  components: {
    Avatar,
    SimpleActionDropDown
  },
  props: {
    group: {
      type: Object,
      required: true
    },
    selectedGroups: {
      type: Array,
      default: () => []
    },
    selectedCcGroups: {
      type: Array,
      default: () => []
    },
    selectedBccGroups: {
      type: Array,
      default: () => []
    },
    groupType: {
      type: String,
      required: true
    },
    ellipsisVerticalIcon: {
      type: Object,
      required: true
    }
  },
  emits: ["deleteGroup", "moveToRecipients", "moveToCc", "moveToBcc", "editContacts"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const canShowDropdown = computed(() => {
      return (
        !props.selectedGroups.some((rGroup) => rGroup.id === props.group.id) ||
        !props.selectedCcGroups.some((ccGroup) => ccGroup.id === props.group.id) ||
        !props.selectedBccGroups.some((bccGroup) => bccGroup.id === props.group.id)
      )
    })

    const dropdownItems = computed(() => {
      let items = []
      if (props.groupType === "recipients") {
        items.push({
          name: t("Move to CC"),
          hidden: props.selectedCcGroups.some((ccGroup) => ccGroup.id === props.group.id),
          action: () => emit("moveToCc", props.group)
        })
        items.push({
          name: t("Move to BCC"),
          hidden: props.selectedBccGroups.some((bccGroup) => bccGroup.id === props.group.id),
          action: () => emit("moveToBcc", props.group)
        })
      } else if (props.groupType === "cc") {
        items.push({
          name: t("Move to Recipients"),
          hidden: props.selectedGroups.some((rGroup) => rGroup.id === props.group.id),
          action: () => emit("moveToRecipients", props.group)
        })
        items.push({
          name: t("Move to BCC"),
          hidden: props.selectedBccGroups.some((bccGroup) => bccGroup.id === props.group.id),
          action: () => emit("moveToBcc", props.group)
        })
      } else if (props.groupType === "bcc") {
        items.push({
          name: t("Move to Recipients"),
          hidden: props.selectedGroups.some((rGroup) => rGroup.id === props.group.id),
          action: () => emit("moveToRecipients", props.group)
        })
        items.push({
          name: t("Move to CC"),
          hidden: props.selectedCcGroups.some((ccGroup) => ccGroup.id === props.group.id),
          action: () => emit("moveToCc", props.group)
        })
      }
      items.push({
        name: t("Edit Contacts"),
        action: () => emit("editContacts", props.groupType, props.group, true)
      })
      items.push({
        name: t("Remove Group"),
        action: () => emit("deleteGroup", props.group)
      })
      return items
    })

    return {
      canShowDropdown,
      dropdownItems
    }
  }
})
</script>
